import { type ReactNode, useEffect, useMemo, useState } from "react";
import { AttachmentContext, useAttachmentContext } from "./AttachmentControls.tsx";
import { useAttachments, useConfiguredDraft } from "./queries.ts";
import { ReferenceUi } from "./reference-ui.ts";

/** One live authorization observer for every surface of the open conversation. */
export function AttachmentWorkspace(props: { conversationId: string | null; children: ReactNode }) {
  const current = useAttachmentContext();
  if (!props.conversationId || current?.conversationId === props.conversationId) {
    return props.children;
  }
  return (
    <AuthorizedWorkspace key={props.conversationId} conversationId={props.conversationId}>
      {props.children}
    </AuthorizedWorkspace>
  );
}

function AuthorizedWorkspace(props: { conversationId: string; children: ReactNode }) {
  const { data, error, refetch } = useAttachments(props.conversationId);
  // Configured, never subscribed: a keystroke in the composer does not re-render the workspace.
  const draft = useConfiguredDraft(props.conversationId);
  useEffect(() => {
    if (data) {
      draft.reconcile(data);
    }
  }, [data, draft]);
  const [ui] = useState(() => new ReferenceUi());
  const value = useMemo(
    () => ({ conversationId: props.conversationId, list: data, error, refetch, ui }),
    [props.conversationId, data, error, refetch, ui],
  );
  return <AttachmentContext value={value}>{props.children}</AttachmentContext>;
}
